import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/services/apiGateway';
import { useAuthStore } from '@/store/authStore';
import { GlobalHeader } from '@/components/GlobalHeader';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from 'react-i18next';

// Backend returns correct_index; we normalise to correctAnswer string on receipt
interface RawQuestion {
  question: string;
  options: string[];
  correct_index: number;
}

interface Question {
  question: string;
  options: string[];
  correctAnswer: string;
}

interface QuizApiResponse {
  questions: RawQuestion[];
  subject: string;
  topic: string;
}

interface SessionResponse {
  session_id: string;
  streak_count: number;
}

const COUNTS = [5, 10, 15] as const;

export function QuizSession() {
  const isOffline = useNetworkStatus();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { childId, setStreakCount } = useAuthStore();

  const subject = searchParams.get('subject') ?? '';
  const theme   = searchParams.get('theme')   ?? subject;

  // ── State ─────────────────────────────────────────────────────────────────
  const [questionCount,  setQuestionCount]  = useState<number | null>(null);
  const [questions,      setQuestions]      = useState<Question[]>([]);
  const [currentIndex,   setCurrentIndex]   = useState(0);
  const [score,          setScore]          = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isAnswered,     setIsAnswered]     = useState(false);
  const [isComplete,     setIsComplete]     = useState(false);
  const [isLoading,      setIsLoading]      = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [newStreak,      setNewStreak]      = useState<number | null>(null);

  const sessionStartRef   = useRef<number>(Date.now());
  const sessionLoggedRef  = useRef(false);

  // ── Fetch quiz from Claude ─────────────────────────────────────────────────
  const fetchQuiz = async (count: number) => {
    setQuestionCount(count);
    setIsLoading(true);
    setError(null);
    sessionStartRef.current = Date.now();
    try {
      const res = await apiFetch<QuizApiResponse>('/ai/quiz', {
        method: 'POST',
        body: JSON.stringify({ subject, topic: theme, count }),
      });
      const normalised: Question[] = (res.questions ?? []).map((q) => ({
        question: q.question,
        options: q.options,
        correctAnswer: q.options[q.correct_index] ?? q.options[0] ?? '',
      }));
      setQuestions(normalised);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('quiz.generate_failed');
      setError(msg.includes('429') ? t('ai_session.limit_reached') : msg);
      setQuestionCount(null);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Answer a question ──────────────────────────────────────────────────────
  const handleAnswer = (option: string) => {
    if (isAnswered) return;
    const q = questions[currentIndex];
    if (!q) return;

    setSelectedOption(option);
    setIsAnswered(true);
    if (option === q.correctAnswer) setScore((s) => s + 1);

    setTimeout(() => {
      const next = currentIndex + 1;
      if (next >= questions.length) {
        setIsComplete(true);
      } else {
        setCurrentIndex(next);
        setSelectedOption(null);
        setIsAnswered(false);
      }
    }, 1500);
  };

  // ── Log session once complete ──────────────────────────────────────────────
  useEffect(() => {
    if (!isComplete || !childId || sessionLoggedRef.current) return;
    sessionLoggedRef.current = true;
    const elapsedMin = Math.round((Date.now() - sessionStartRef.current) / 60_000);
    const duration_minutes = Math.max(5, Math.min(240, elapsedMin || (questionCount ?? 5) * 2));
    apiFetch<SessionResponse>(`/children/${childId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({
        subject,
        topic: theme,
        format: 'standard',
        duration_minutes,
        note: `Quiz: ${score}/${questions.length} correct`,
      }),
    })
      .then((res) => {
        setNewStreak(res.streak_count);
        setStreakCount(res.streak_count);
      })
      .catch(() => { /* best-effort */ });
  }, [isComplete, childId, subject, theme, score, questions.length, questionCount, setStreakCount]);

  // ── Option button style ────────────────────────────────────────────────────
  const optionStyle = (option: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      width: '100%',
      minHeight: '48px',
      padding: '12px 16px',
      textAlign: 'left',
      borderRadius: '10px',
      border: '1.5px solid var(--border)',
      background: 'white',
      fontFamily: 'inherit',
      fontSize: '0.9rem',
      color: 'var(--text-dark)',
      cursor: isAnswered ? 'default' : 'pointer',
      transition: 'border-color 0.15s, background 0.15s',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      lineHeight: 1.4,
    };
    if (!isAnswered) return base;
    const q = questions[currentIndex];
    if (!q) return base;
    if (option === q.correctAnswer)
      return { ...base, background: '#F0FDF4', borderColor: 'var(--success)', color: 'var(--success)', fontWeight: 700 };
    if (option === selectedOption)
      return { ...base, background: '#FEF2F2', borderColor: 'var(--danger)', color: 'var(--danger)' };
    return { ...base, opacity: 0.4 };
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Step 1 — Loading skeleton
  // ─────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack backRoute="/student" customTitle={t('quiz.header_title')} />
          <div className="app-content" style={{ gap: '16px' }}>
            <div className="loading-skeleton" style={{ height: '120px', borderRadius: '12px' }} />
            <div className="loading-skeleton" style={{ height: '48px', borderRadius: '10px' }} />
            <div className="loading-skeleton" style={{ height: '48px', borderRadius: '10px' }} />
            <div className="loading-skeleton" style={{ height: '48px', borderRadius: '10px' }} />
            <div className="loading-skeleton" style={{ height: '48px', borderRadius: '10px' }} />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', margin: 0 }}>
              {t('quiz.generating')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Step 2 — Setup screen
  // ─────────────────────────────────────────────────────────────────────────
  if (questionCount === null) {
    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack backRoute="/student" customTitle={t('quiz.header_title')} />
          <div className="app-content">

            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
              {t('quiz.title')}
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0 0 24px' }}>
              {subject && <><strong>{subject}</strong>{theme && theme !== subject && <> · {theme}</>}</>}
            </p>

            {error && (
              <p style={{
                color: 'var(--danger)', fontSize: '0.85rem', margin: '0 0 16px',
                padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px',
              }}>
                ⚠️ {error}
              </p>
            )}

            <div className="card">
              <p style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-dark)', margin: '0 0 16px' }}>
                {t('quiz.choose')}
              </p>
              {isOffline ? (
                <button type="button" className="btn btn-outline" disabled
                  style={{ width: '100%', padding: '14px 16px', opacity: 0.5 }}>
                  {t('ai_session.offline_cta')}
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {COUNTS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="btn btn-outline"
                      onClick={() => { void fetchQuiz(n); }}
                      style={{ width: '100%', padding: '14px 16px', textAlign: 'left', fontSize: '0.95rem' }}
                    >
                      <span style={{ fontWeight: 700 }}>{t('quiz.questions', { count: n })}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginLeft: '8px' }}>
                        · ~{n * 2} min
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Step 4 — Completion screen
  // ─────────────────────────────────────────────────────────────────────────
  if (isComplete) {
    const total = questions.length;
    const pct   = total > 0 ? Math.round((score / total) * 100) : 0;
    const emoji = pct >= 80 ? '🌟' : pct >= 60 ? '👍' : '📚';
    const label = pct >= 80
      ? t('quiz.result_excellent')
      : pct >= 60
        ? t('quiz.result_good')
        : t('quiz.result_keep_practising');

    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack backRoute="/student" customTitle={t('quiz.results_title')} />
          <div className="app-content" style={{ alignItems: 'center', textAlign: 'center' }}>

            <div style={{ fontSize: '3.5rem', marginBottom: '8px' }}>🎉</div>

            <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 24px' }}>
              {t('quiz.complete_title')}
            </h1>

            {/* Score card */}
            <div style={{
              background: 'var(--primary-light, #EEF2FF)',
              borderRadius: '20px',
              padding: '32px 24px',
              width: '100%',
              marginBottom: '24px',
              boxSizing: 'border-box',
            }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('quiz.your_score')}
              </p>
              <p style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--primary)', margin: '0 0 4px', lineHeight: 1 }}>
                {score}/{total}
              </p>
              <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)', margin: 0 }}>
                {t('quiz.pct_correct', { pct })}
              </p>
            </div>

            <p style={{ fontSize: '1rem', color: 'var(--text-dark)', fontWeight: 600, margin: '0 0 8px' }}>
              {emoji} {label}
            </p>

            {newStreak !== null && (
              <div className="streak-badge" style={{ display: 'inline-block', fontSize: '0.95rem', padding: '6px 18px', marginBottom: '24px' }}>
                🔥 {t('session_complete.streak', { count: newStreak })}
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/student')}
              style={{ width: '100%', marginTop: '8px' }}
            >
              {t('session_complete.back_dash')}
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setQuestionCount(null);
                setQuestions([]);
                setCurrentIndex(0);
                setScore(0);
                setSelectedOption(null);
                setIsAnswered(false);
                setIsComplete(false);
                sessionLoggedRef.current = false;
              }}
              style={{ width: '100%', marginTop: '10px' }}
            >
              {t('quiz.try_another')}
            </button>

          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Step 3 — Active question
  // ─────────────────────────────────────────────────────────────────────────
  const q        = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex) / questions.length) * 100 : 0;

  if (!q) return null;

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student" customTitle={`${currentIndex + 1} / ${questions.length}`} />

        <div className="app-content" style={{ gap: '16px' }}>

          {/* Progress bar */}
          <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              background: 'var(--primary)',
              borderRadius: '2px',
              width: `${progress}%`,
              transition: 'width 0.4s ease',
            }} />
          </div>

          {/* Subject label */}
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
            {subject}
          </p>

          {/* Question card */}
          <div className="card" style={{ padding: '20px' }}>
            <p style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-dark)', margin: 0, lineHeight: 1.55 }}>
              {q.question}
            </p>
          </div>

          {/* Options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {q.options.map((option, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleAnswer(option)}
                disabled={isAnswered}
                style={optionStyle(option)}
              >
                <span style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  color: 'var(--text-dark)',
                }}>
                  {['A', 'B', 'C', 'D'][idx]}
                </span>
                {option}
              </button>
            ))}
          </div>

          {/* Running score */}
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>
            {t('quiz.running_score', { score, total: currentIndex + (isAnswered ? 1 : 0) })}
          </p>

        </div>
      </div>
    </div>
  );
}
